import { Dispatch, RefObject, SetStateAction } from "react";
import { FormGroup, Input } from "reactstrap";
import { SelectPicker } from "rsuite";

import {
  BrieGPTConfig,
  CalendarConfig,
  QrCodeConfig,
  ThemeSelector,
  ViewTypesWithField,
  ViewTypesWithModule,
  ViewTypesWithModuleSection,
} from "./AddViewDrawer";
import {
  Field,
  LayoutAndSections,
  Module,
} from "../../../types/TenantInterfaces";
import { AllViewTypes, ReusableViewIDs } from "../../../types/PageInterfaces";
import ReusableViewConfig from "../PageViewReusableView/ReusableViewConfig";
import CalendarConfigComponent from "../PageViewTable/CalendarConfig";
import BrieGPTViewConfig from "../PageViewBrieGPT/BrieGPTViewConfig";
import RequiredTitle from "../../../elements/RequiredTitle";
import { PageView } from "../../../types/GlobalInterfaces";
import ModulePicker from "../../Modules/ModulePicker";
import useModule from "../../../hooks/useModule";
import DocumentSection from "./DocumentSection";

function FilterByViewType(viewType: string) {
  let valid: string[] = [];

  if (viewType === "Gallery") valid = ["SingleFile"];
  if (viewType === "RichText") valid = ["RichText"];
  if (viewType === "Subitem") valid = ["MultiSubitem", "Subitem"];

  return (f: Field) => valid.includes(f.FieldType);
}

export type DocumentPageType = "Single" | "Multiple";

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  viewType: AllViewTypes;
  moduleAPIName: string;
  setModuleAPIName: Dispatch<SetStateAction<string>>;
  field: string;
  setField: Dispatch<SetStateAction<string>>;
  link: string;
  setLink: Dispatch<SetStateAction<string>>;
  query: any;
  setQuery: any;
  theme: string;
  setTheme: Dispatch<SetStateAction<string>>;
  insights: boolean;
  setInsights: Dispatch<SetStateAction<boolean>>;
  recordModule: Module | undefined;
  calendarConfig: CalendarConfig;
  setCalendarConfig: Dispatch<SetStateAction<CalendarConfig>>;
  layout: LayoutAndSections | null;
  setLayout: Dispatch<SetStateAction<LayoutAndSections | null>>;
  documentPageType: DocumentPageType | undefined;
  setDocumentPageType: Dispatch<SetStateAction<DocumentPageType>>;
  document: string;
  setDocument: Dispatch<SetStateAction<string>>;
  qrCodeConfig: QrCodeConfig | null;
  setQrCodeConfig: Dispatch<SetStateAction<QrCodeConfig | null>>;
  brieGPTConfig: BrieGPTConfig;
  setBrieGPTConfig: Dispatch<SetStateAction<BrieGPTConfig>>;
  globalAppID: string | undefined;
  setReuseViewID: Dispatch<SetStateAction<string>>;
  reuseViewID: string;
  pageID: string;
  parentViewID?: string;
  subViews?: PageView[];
  reusableViews?: ReusableViewIDs;
  setReusableViews?: Dispatch<SetStateAction<ReusableViewIDs>>;
}

export default function ViewSpecificSection({
  containerRef,
  viewType,
  moduleAPIName,
  setModuleAPIName,
  field,
  setField,
  link,
  setLink,
  query,
  setQuery,
  theme,
  setTheme,
  insights,
  setInsights,
  recordModule,
  calendarConfig,
  setCalendarConfig,
  layout,
  setLayout,
  documentPageType,
  setDocumentPageType,
  document,
  setDocument,
  qrCodeConfig,
  setQrCodeConfig,
  brieGPTConfig,
  setBrieGPTConfig,
  globalAppID,
  setReuseViewID,
  reuseViewID,
  pageID,
  parentViewID,
  reusableViews,
  setReusableViews,
  subViews,
}: Props) {
  const { module } = useModule(moduleAPIName);
  const handleLinkChange = (e: any) => setLink(e.target.value);

  return (
    <>
      {ViewTypesWithModule.includes(viewType) && (
        <>
          <RequiredTitle title="Data Source" size="md" />
          <ModulePicker
            value={moduleAPIName}
            onChange={setModuleAPIName}
            defaultOpen={!moduleAPIName}
            containerRef={containerRef}
          />
        </>
      )}
      {ViewTypesWithModule.includes(viewType) && moduleAPIName && module && (
        <ViewTypesWithModuleSection
          query={query}
          setQuery={setQuery}
          moduleAPIName={moduleAPIName}
          module={module}
          layout={layout}
          setLayout={setLayout}
          insights={insights}
          setInsights={setInsights}
          qrCodeConfig={qrCodeConfig}
          setQrCodeConfig={setQrCodeConfig}
          globalAppID={globalAppID}
          viewID={undefined}
          reusableViews={reusableViews}
          setReusableViews={setReusableViews}
        />
      )}
      {viewType === "Document" && (
        <DocumentSection
          documentPageType={documentPageType}
          setDocumentPageType={setDocumentPageType}
          document={document}
          setDocument={setDocument}
        />
      )}
      {viewType === "External" && (
        <FormGroup>
          <RequiredTitle title="External Link" size="md" />
          <Input
            placeholder="https://growthcloud.com"
            type="text"
            value={link}
            onChange={handleLinkChange}
            bsSize="sm"
          />
        </FormGroup>
      )}
      {ViewTypesWithField.includes(viewType) && (
        <FormGroup>
          <RequiredTitle title="Field" size="md" />
          <SelectPicker
            data={recordModule?.Fields.filter(FilterByViewType(viewType)) || []}
            labelKey="Name"
            valueKey="APIName"
            value={field}
            onChange={(f) => setField(f || "")}
            onClean={() => setField("")}
            preventOverflow
            block
            placeholder="Select Field"
          />
        </FormGroup>
      )}
      {(viewType === "Dashboard" || insights) && (
        <ThemeSelector theme={theme} setTheme={setTheme} />
      )}
      {viewType === "Calendar" && (
        <CalendarConfigComponent
          moduleAPIName={moduleAPIName}
          calendarConfig={calendarConfig}
          setCalendarConfig={setCalendarConfig}
        />
      )}
      {viewType === "ReuseView" && (
        <ReusableViewConfig
          globalAppID={globalAppID}
          setReuseViewID={setReuseViewID}
          reuseViewID={reuseViewID}
          pageID={pageID}
          parentViewID={parentViewID}
          subViews={subViews}
        />
      )}
      {viewType === "BrieGPT" && (
        <BrieGPTViewConfig
          brieGPTConfig={brieGPTConfig}
          setBrieGPTConfig={setBrieGPTConfig}
        />
      )}
    </>
  );
}
